/**
 * Safe wrappers around the cosmetic cross-domain xprops helpers (page scroll + layout spacers).
 *
 * These invoke functions that zoid proxies to the PARENT window over post-robot. Under real
 * cross-domain conditions (staging, unlike the local mockXprops path) a call can intermittently
 * reject — or throw synchronously — from inside the post-robot runtime, e.g. "w is not a function"
 * during the prerender→render handshake window. Every one of these calls is purely cosmetic, so a
 * failure must never surface as an uncaught error or unhandled promise rejection in the console.
 *
 * `swallow` invokes the call synchronously (preserving the original call timing) inside a
 * try/catch for synchronous throws, and attaches a `.catch` for async rejections, discarding
 * both. Callers get a promise that never rejects.
 */
const swallow = (call: () => Promise<void> | undefined): Promise<void> => {
  try {
    return Promise.resolve(call()).then((): void => undefined, (): void => undefined);
  } catch {
    return Promise.resolve();
  }
};

export const scrollToWidget = (): Promise<void> => (
  swallow((): Promise<void> => window.xprops.scrollToWidget())
);

export const scrollTo = (options: ScrollToOptions): Promise<void> => (
  swallow((): Promise<void> => window.xprops.scrollTo(options))
);

export const autoCalculateSpacers = (): Promise<void> => (
  swallow((): Promise<void> => window.xprops.autoCalculateSpacers())
);
